<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedWorkers"
      multi-select
      searchable
      :label="meta.filter.quickFilter ? '' : $t('Worker')"
      :list="workerOptions"
      :placeholder="$t('All workers')"
      max-labels-visible="5"
      can-deselect
      labels-primary
      :loading="isLoading"
    />
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack } from '@worksome/wds-components'
import { computed, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'
import { graphql } from '@/gql'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

defineProps<{ meta: FilterMetaDataProp }>()

const selectedWorkers: ModelRef<{ label: string; value: string }[]> = defineModel({
  default: () => [],
})

const { actsAsSupplier } = useAccountCapabilities()

const { result, loading } = useQuery(
  graphql(`
    query RecruiterWorkersFilter($id: ID!) {
      recruiter(id: $id) {
        id
        candidates {
          id
          worker {
            id
            name
          }
        }
      }
    }
  `),
  () => ({
    id: currentAccount.globalId,
  }),
  () => ({
    clientId: 'publicApi',
    enabled: !actsAsSupplier.value,
  }),
)

const { result: supplierResult, loading: supplierLoading } = useQuery(
  graphql(`
    query SupplierPlacementWorkersFilter($supplierAccounts: [ID!], $first: Int!) {
      hires(supplierAccounts: $supplierAccounts, first: $first) {
        data {
          id
          worker {
            id
            name
          }
        }
      }
    }
  `),
  () => ({
    supplierAccounts: [currentAccount.globalId],
    first: 500,
  }),
  () => ({
    clientId: 'publicApi',
    enabled: actsAsSupplier.value,
  }),
)

const isLoading = computed(() => (actsAsSupplier.value ? supplierLoading.value : loading.value))

const workerOptions = computed(() => {
  const seen = new Set<string>()

  if (actsAsSupplier.value) {
    return (
      supplierResult.value?.hires.data
        ?.filter(({ worker }) => {
          if (seen.has(worker.id)) return false
          seen.add(worker.id)
          return true
        })
        .map(({ worker }) => ({
          label: worker.name,
          value: worker.id,
        })) ?? []
    )
  }

  return (
    result.value?.recruiter?.candidates
      ?.filter(({ worker }) => {
        if (seen.has(worker.id)) return false
        seen.add(worker.id)
        return true
      })
      .map(({ worker }) => ({
        label: worker.name,
        value: worker.id,
      })) ?? []
  )
})
</script>
